import { ScriptHTMLAttributes, useEffect, useState } from 'react'

const cachedScripts = new Set<string>()

const createElementWithAttributes = (elName, options) => {
  const el = document.createElement(elName)

  for (const key in options) {
    if (options.hasOwnProperty(key)) {
      el.setAttribute(key, options[key])
    }
  }

  return el
}

type ScriptLoaderProps = {
  scriptPlacement: 'body' | 'head'
} & ScriptHTMLAttributes<HTMLScriptElement>

const useScript = ({ scriptPlacement, src, ...scriptOptions }: ScriptLoaderProps) => {
  const [state, setState] = useState({
    loaded: false,
    error: false,
  })

  useEffect(() => {
    if (!src) {
      return
    }

    if (cachedScripts.has(src)) {
      setState({
        loaded: true,
        error: false,
      })

      return
    }

    cachedScripts.add(src)

    const script = createElementWithAttributes('script', { src, ...scriptOptions })

    const onScriptLoad = () => {
      setState({
        loaded: true,
        error: false,
      })
    }

    const onScriptError = () => {
      cachedScripts.delete(src)
      script.remove()
    }

    script.addEventListener('load', onScriptLoad)
    script.addEventListener('error', onScriptError)

    // Add script to document body
    document[scriptPlacement].appendChild(script)

    return () => {
      script.removeEventListener('load', onScriptLoad)
      script.removeEventListener('error', onScriptError)

      // Make sure to clean up if src is changed
      cachedScripts.delete(src)
      script.remove()
    }
  }, [src])

  return [state.loaded, state.error]
}

// Wrapper that can be used in class based components.
export const ScriptLoader = (options: ScriptLoaderProps) => {
  // TODO add retry mechanism if failed.
  // const [loaded, error] = useScript(options)
  useScript(options)
  return null
}

export default useScript
